import type { TOC } from '@ember/component/template-only';
import { VStack } from './layout.gts';

interface FilterRailSignature {
  Args: {
    /** Accessible name for the rail (the visual heading is intentionally
     * omitted — pass a translated string, e.g. `{{t "people.filters"}}`). */
    label: string;
  };
  Element: HTMLElement;
  Blocks: { default: [] };
}

/**
 * Left-hand filter rail for index/listing pages.
 *
 * Owns the chrome that makes filter controls legible on the gray page
 * background: a `--bg-surface` card with a default border (inputs use the
 * recessed `--bg-sunken` fill, which is invisible directly on `--bg-page`),
 * plus sticky positioning at `lg`+ so filters stay in view while scrolling
 * results. Below `lg` the rail is static and stacks above the results —
 * never hide it on small screens.
 *
 * The caller owns page-layout concerns only (grid column / width), e.g.:
 *
 * ```gts
 * <div class="grid grid-cols-1 lg:grid-cols-5 items-start gap-6">
 *   <FilterRail @label={{t "people.filters"}} class="lg:col-span-1">
 *     <Select @label={{t "people.filter_by_partner"}} ... />
 *     <Switch @label={{t "people.enrolled"}} ... />
 *   </FilterRail>
 *   <div class="lg:col-span-4 min-w-0">...results...</div>
 * </div>
 * ```
 *
 * @public
 */
const FilterRail: TOC<FilterRailSignature> = <template>
  <aside data-test-filter-rail class="app-filter-rail" aria-label={{@label}} ...attributes>
    {{! collapsed — VStack's default 1rem padding would stack on top of the
        rail's own padding }}
    <VStack @collapsed={{true}}>
      {{yield}}
    </VStack>
  </aside>
</template>;

export default FilterRail;
